import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Handshake,
  Bot,
  CreditCard,
  MoreHorizontal,
  Network,
  Send,
  FileSearch,
  PenTool,
  ImagePlay,
  BarChart3,
  TrendingUp,
  DollarSign,
  Scale,
  Users,
  Target,
  Settings,
  X,
  ChevronRight,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import type { SubscriptionTier } from '@/types/common';
import styles from './layout.module.css';

interface MoreItem {
  label: string;
  icon: React.ReactNode;
  to: string;
  tier?: SubscriptionTier;
}

const MORE_ITEMS: MoreItem[] = [
  { label: 'Affiliate', icon: <Network size={18} />, to: '/affiliate' },
  { label: 'Deal Reviewer', icon: <Bot size={18} />, to: '/deal-reviewer', tier: 'pro' },
  { label: 'Contract Analyzer', icon: <FileSearch size={18} />, to: '/contract-analyzer', tier: 'pro' },
  { label: 'Brand Outreach', icon: <Send size={18} />, to: '/outreach', tier: 'manager' },
  { label: 'Media Kit', icon: <ImagePlay size={18} />, to: '/media-kit' },
  { label: 'Industry Intel', icon: <TrendingUp size={18} />, to: '/industry-intel', tier: 'pro' },
  { label: 'Finances', icon: <DollarSign size={18} />, to: '/finances' },
  { label: 'Legal & Rights', icon: <Scale size={18} />, to: '/legal', tier: 'manager' },
  { label: 'Collabs', icon: <Users size={18} />, to: '/collabs', tier: 'manager' },
  { label: 'My Goals', icon: <Target size={18} />, to: '/goals' },
  { label: 'Settings', icon: <Settings size={18} />, to: '/settings' },
];

const TIER_LABELS: Record<string, string> = {
  pro: 'Pro+',
  manager: 'Manager',
};

export const BottomTabBar: React.FC = () => {
  const { mobileMoreSheetOpen, setMobileMoreSheetOpen, toggleAIPanel } = useUIStore();
  const { profile } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMoreSheetOpen) setMobileMoreSheetOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileMoreSheetOpen, setMobileMoreSheetOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileMoreSheetOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMoreSheetOpen]);

  const handleMoreItemClick = (to: string) => {
    setMobileMoreSheetOpen(false);
    navigate(to);
  };

  return (
    <>
      {/* Bottom Tab Bar */}
      <nav className={styles.bottomBar} aria-label="Bottom navigation">
        <NavLink
          to="/"
          end
          className={({ isActive }) => `${styles.bottomTab} ${isActive ? styles.bottomTabActive : ''}`}
          aria-label="Dashboard"
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </>
          )}
        </NavLink>

        <NavLink
          to="/deals"
          className={({ isActive }) => `${styles.bottomTab} ${isActive ? styles.bottomTabActive : ''}`}
          aria-label="Deals"
        >
          {({ isActive }) => (
            <>
              <Handshake size={20} />
              <span>Deals</span>
            </>
          )}
        </NavLink>

        {/* AI Chat — center */}
        <div className={styles.bottomTabAI}>
          <button
            onClick={toggleAIPanel}
            className={styles.bottomTabAIBtn}
            aria-label="Open AI Manager"
          >
            <Bot size={22} />
          </button>
          <span className={styles.bottomTabAILabel}>AI Chat</span>
        </div>

        <NavLink
          to="/payments"
          className={({ isActive }) => `${styles.bottomTab} ${isActive ? styles.bottomTabActive : ''}`}
          aria-label="Payments"
        >
          {({ isActive }) => (
            <>
              <CreditCard size={20} />
              <span>Payments</span>
            </>
          )}
        </NavLink>

        <button
          onClick={() => setMobileMoreSheetOpen(true)}
          className={`${styles.bottomTab} ${mobileMoreSheetOpen ? styles.bottomTabActive : ''}`}
          aria-label="More navigation options"
          aria-expanded={mobileMoreSheetOpen}
        >
          <MoreHorizontal size={20} />
          <span>More</span>
        </button>
      </nav>

      {/* More Sheet Backdrop */}
      {mobileMoreSheetOpen && (
        <div
          className={styles.moreBackdrop}
          onClick={() => setMobileMoreSheetOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* More Sheet */}
      <div
        className={`${styles.moreSheet} ${mobileMoreSheetOpen ? styles.moreSheetOpen : styles.moreSheetClosed}`}
        role="dialog"
        aria-label="More navigation"
        aria-modal="true"
      >
        <div className={styles.moreHandle}>
          <div className={styles.moreHandleBar} />
        </div>

        <div className={styles.moreHeader}>
          <h2 className={styles.moreHeaderTitle}>More</h2>
          <button
            onClick={() => setMobileMoreSheetOpen(false)}
            className={styles.moreCloseBtn}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className={styles.moreItems}>
          {MORE_ITEMS.map((item) => (
            <button
              key={item.to}
              onClick={() => handleMoreItemClick(item.to)}
              className={styles.moreItem}
            >
              <span className={styles.moreItemIcon}>{item.icon}</span>
              <span className={styles.moreItemLabel}>{item.label}</span>
              {item.tier && (
                <span className={styles.moreItemBadge}>{TIER_LABELS[item.tier]}</span>
              )}
              <ChevronRight size={14} className={styles.moreItemChevron} />
            </button>
          ))}
        </div>

        {profile && (
          <div className={styles.moreUserRow}>
            <div className={styles.sidebarAvatar}>
              {profile.full_name?.charAt(0) ?? 'U'}
            </div>
            <div>
              <p className={styles.sidebarUserName}>{profile.display_name ?? profile.full_name}</p>
              <p className={styles.sidebarUserTier}>{profile.subscription_tier} plan</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
